import * as React from 'react';
import { IconContext, type IconProps } from '@phosphor-icons/react';

/**
 * The brand sized for a thumb (`.theme-phone` in packages/tokens): 48px
 * fields, larger type. The colours and the face are the brand's, the
 * defaults every app already has; this changes only size. Only in the mobile
 * app and the phone specimens in /library.
 */
export const PHONE_THEME_CLASS = 'theme-phone';

/**
 * Puts the whole document at phone sizes while the calling component is
 * mounted. On <html> rather than the screen's root because sheets, menus and
 * toasts portal to <body>, outside any screen, and a class on the screen
 * would leave them at the desk's sizes. The driver app's old screens key
 * their own look on its absence.
 */
export function useMobileTheme() {
  React.useEffect(() => {
    const root = document.documentElement;
    root.classList.add(PHONE_THEME_CLASS);
    return () => root.classList.remove(PHONE_THEME_CLASS);
  }, []);
}

/**
 * The phone Figma's icons are Phosphor at the bold weight: a 20px glyph
 * drawn at a 1.875 stroke, the round buttons', the comment and close
 * marks'. Phosphor's default is regular, two-thirds as thick, which is why
 * every icon in the app read thinner and lighter than the design. A glyph
 * that asks for its own weight (the presence pill's filled person) keeps it.
 */
const PHONE_ICONS: IconProps = { weight: 'bold' };

/** Phosphor icons at the phone's weight, for anything drawn as the phone. */
export function PhoneIconStyle({ children }: { children: React.ReactNode }) {
  return (
    <IconContext.Provider value={PHONE_ICONS}>{children}</IconContext.Provider>
  );
}

/**
 * A screen drawn as the phone app: the brand at phone sizes on the whole
 * document (see useMobileTheme for why <html>) and the Figma's icon weight
 * for everything inside, sheets and toasts included, since React's context
 * follows a portal where a class on the screen would not.
 *
 *   <MobileTheme>
 *     <MobileShell … />
 *   </MobileTheme>
 */
export function MobileTheme({ children }: { children: React.ReactNode }) {
  useMobileTheme();
  return <PhoneIconStyle>{children}</PhoneIconStyle>;
}
